import { useEffect, useMemo } from 'react';
import { normalizeRestrictionsPayload, composeRestrictions } from 'utils/filters';
import { RestrictionsPayload } from 'common/types/filters';
import { useGetAllRestrictions } from 'common/queries/filters';
import { DEFAULT_RESTRICTIONS_PAYLOAD, normalizeRawRestrictions, removeStudyFromRestrictions } from './filters/utils/normalizers';
import { restrictionsState } from 'common/state/filters';
import { useSetRecoilState } from 'recoil';

export const useGetStudySpecificRestrictions = (studyUuid: string): RestrictionsPayload & { isPlaceholderData: boolean } => {
  const { data: restrictionsData, isPlaceholderData } = useGetAllRestrictions();
  const setRestrictions = useSetRecoilState(restrictionsState(studyUuid));

  const normalizedRestrictionsData = useMemo(() => {
    return normalizeRawRestrictions(restrictionsData || []);
  }, [restrictionsData]);

  const restrictions = useMemo(() => {
    if (!studyUuid) {
      return DEFAULT_RESTRICTIONS_PAYLOAD;
    }

    const studyRestrictions = normalizedRestrictionsData.find(({ study_uuid }) => study_uuid === studyUuid);

    if (!studyRestrictions) {
      console.warn(`No restrictions have been found for study ${studyUuid}`);
      return DEFAULT_RESTRICTIONS_PAYLOAD;
    }

    const restrictionsWithoutStudy = removeStudyFromRestrictions(studyRestrictions);
    const normalizedRestrictions = normalizeRestrictionsPayload(restrictionsWithoutStudy);

    return normalizedRestrictions;
  }, [normalizedRestrictionsData, studyUuid]);

  useEffect(() => {
    if (!isPlaceholderData && studyUuid) {
      setRestrictions(restrictions);
    }
  }, [restrictions, setRestrictions, isPlaceholderData, studyUuid]);

  return useMemo(() => ({ ...restrictions, isPlaceholderData: isPlaceholderData ?? false }), [restrictions, isPlaceholderData]);
};

export const useGetAllFilters = (): RestrictionsPayload => {
  const { data: allRestrictionsData } = useGetAllRestrictions();

  return useMemo(() => {
    const restritctionsWithoutStudyId = (allRestrictionsData || []).map(removeStudyFromRestrictions);
    const restrictions = restritctionsWithoutStudyId?.reduce(composeRestrictions, DEFAULT_RESTRICTIONS_PAYLOAD);
    return normalizeRestrictionsPayload(restrictions);
  }, [allRestrictionsData]);
};
